import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  Select,
  HStack,
  VStack,
  Grid,
  GridItem,
  Tag,
  Link,
  Spinner,
} from '@chakra-ui/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useRocks } from '../Rocks/useRocks';
import { Rock, User, ActionItem } from '../types';
import { Card } from '../../../components/Card';

// This interface matches the logic from your original Collaborators.tsx
interface CollaborationRecord {
  rockId: string;
  rockTitle: string;
  rockNotes: string;
  rockStatus: 'on-track' | 'off-track' | 'at-risk';
  quarter: string;
  year: number;
  userId: string;
  userName: string;
  collaborationType: 'R' | 'T' | 'R,T';
}

/**
 * Collaborators Page
 * Displays a filterable grid of all rock collaborations (as Rock collaborator or To-Do assignee)
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function CollaboratorsPage() {
  const history = useHistory();
  const { currentUser } = useAuth();
  const { rocks, users, loading, loadActionItemsForRocks } = useRocks(currentUser?.uid);

  const [actionItemsMap, setActionItemsMap] = useState<Map<string, ActionItem[]>>(new Map());
  const [loadingActionItems, setLoadingActionItems] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string>('all');

  // Step 1: Load all action items for all rocks once rocks are loaded
  useEffect(() => {
    if (rocks.length > 0 && loadActionItemsForRocks) {
      setLoadingActionItems(true);
      const rockIds = rocks.map(r => r.id);
      loadActionItemsForRocks(rockIds)
        .then(map => {
          setActionItemsMap(map);
        })
        .catch(error => {
          console.error("Error loading action items for collaborators page:", error);
        })
        .finally(() => {
          setLoadingActionItems(false);
        });
    } else if (!loading) {
      setLoadingActionItems(false);
    }
  }, [rocks, loadActionItemsForRocks, loading]);

  // Step 2: Build the collaboration records (logic migrated from original)
  const collaborationRecords = useMemo(() => {
    const records: CollaborationRecord[] = [];
    const userMap = new Map(users.map(u => [u.id, u.name]));

    rocks.forEach((rock) => {
      const collaboratorMap = new Map<string, Set<'R' | 'T'>>();
      const rockActionItems = actionItemsMap.get(rock.id) || [];

      // Add 'R' type for formal collaborators
      rock.collaborators.forEach((userId) => {
        if (!collaboratorMap.has(userId)) {
          collaboratorMap.set(userId, new Set());
        }
        collaboratorMap.get(userId)!.add('R');
      });

      // Add 'T' type for incomplete Action Item assignees
      const incompleteActionItems = rockActionItems.filter(
        (item) => !item.complete && item.assigneeId
      );

      incompleteActionItems.forEach((item) => {
        if (item.assigneeId) {
          if (!collaboratorMap.has(item.assigneeId)) {
            collaboratorMap.set(item.assigneeId, new Set());
          }
          collaboratorMap.get(item.assigneeId)!.add('T');
        }
      });

      // Flatten the map into records
      collaboratorMap.forEach((types, userId) => {
        const userName = userMap.get(userId);
        if (!userName) return; // Skip if user not found

        const typesArray = Array.from(types).sort();
        const collaborationType = typesArray.join(',') as 'R' | 'T' | 'R,T';

        const dueDate = new Date(rock.dueDate);
        const quarter = `Q${Math.ceil((dueDate.getMonth() + 1) / 3)}`;
        const year = dueDate.getFullYear();

        records.push({
          rockId: rock.id,
          rockTitle: rock.rock,
          rockNotes: rock.notes || '',
          rockStatus: rock.status,
          quarter,
          year,
          userId: userId,
          userName: userName,
          collaborationType,
        });
      });
    });

    return records.sort((a, b) => a.userName.localeCompare(b.userName));
  }, [rocks, users, actionItemsMap]);

  // Step 3: Create memoized helper maps for filtering and display
  const userCollaborationCounts = useMemo(() => {
    const counts = new Map<string, number>();
    collaborationRecords.forEach((record) => {
      counts.set(record.userId, (counts.get(record.userId) || 0) + 1);
    });
    return counts;
  }, [collaborationRecords]);

  const filteredRecords = useMemo(() => {
    if (selectedUserId === 'all') return collaborationRecords;
    return collaborationRecords.filter((r) => r.userId === selectedUserId);
  }, [collaborationRecords, selectedUserId]);

  // Step 4: Helper functions for styling and navigation
  const handleRockClick = (rockId: string) => {
    history.push(`/eos/rocks#rocks/${rockId}`);
  };

  const getRockStyling = (status: 'on-track' | 'off-track' | 'at-risk') => {
    if (status === 'on-track') return { bg: 'green.50', borderColor: 'green.200' };
    if (status === 'at-risk') return { bg: 'yellow.50', borderColor: 'yellow.200' };
    return { bg: 'red.50', borderColor: 'red.200' };
  };

  const isLoadingData = loading || loadingActionItems;

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Box mb={6}>
        <Heading size="lg" color="gray.900">
          Collaborators
        </Heading>
        <Text color="gray.600" mt={1}>
          View and filter team member involvement across all Rocks
        </Text>
      </Box>

      {/* Filter Bar */}
      <Flex
        bg="white"
        borderRadius="lg"
        border="1px"
        borderColor="gray.200"
        p={4}
        mb={6}
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={4}
      >
        <HStack spacing={3} flex={1} minW="300px">
          <Text fontSize="sm" fontWeight="medium" color="gray.700" whiteSpace="nowrap">
            Filter by Team Member:
          </Text>
          <Select
            id="user-filter"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            bg="white"
            maxW="400px"
            disabled={isLoadingData}
          >
            <option value="all">
              All Users ({collaborationRecords.length} collaborations)
            </option>
            {users
              .filter(user => (userCollaborationCounts.get(user.id) || 0) > 0)
              .map((user) => {
                const count = userCollaborationCounts.get(user.id) || 0;
                return (
                  <option key={user.id} value={user.id}>
                    {user.name} ({count} collaboration{count !== 1 ? 's' : ''})
                  </option>
                );
            })}
          </Select>
        </HStack>
        <Tag colorScheme="blue" size="lg" borderRadius="full">
          {filteredRecords.length} {filteredRecords.length === 1 ? 'Rock' : 'Rocks'}
        </Tag>
      </Flex>

      {/* Content Grid */}
      {isLoadingData ? (
        <Flex justify="center" align="center" h="300px">
          <Spinner size="xl" />
        </Flex>
      ) : filteredRecords.length === 0 ? (
        <Box textAlign="center" py={12} bg="white" borderRadius="lg" border="1px" borderColor="gray.200">
          <Text color="gray.500" fontSize="lg">
            {collaborationRecords.length === 0
              ? 'Add collaborators to Rocks or Action Items to see them here'
              : 'This user is not currently collaborating on any Rocks'}
          </Text>
        </Box>
      ) : (
        <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={4}>
          {filteredRecords.map((record, index) => {
            const style = getRockStyling(record.rockStatus);
            return (
              <GridItem key={`${record.rockId}-${record.userId}-${index}`}>
                <Card
                  bg={style.bg}
                  border="2px"
                  borderColor={style.borderColor}
                  _hover={{ shadow: 'md', borderColor: style.borderColor }}
                  transition="all 0.2s"
                  p={4} // Use Card's internal padding
                >
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text fontWeight="bold" color="gray.900">
                        {record.userName}
                      </Text>
                      <Tag colorScheme="blue" size="sm" borderRadius="full">
                        {record.collaborationType}
                      </Tag>
                    </HStack>
                    
                    <Link
                      onClick={() => handleRockClick(record.rockId)}
                      color="blue.600"
                      fontWeight="medium"
                      _hover={{ textDecoration: 'underline', color: 'blue.800' }}
                      fontSize="md"
                      lineHeight="short"
                    >
                      {record.rockTitle}
                    </Link>

                    {record.rockNotes && (
                      <Text fontSize="xs" color="gray.600" noOfLines={2}>
                        {record.rockNotes}
                      </Text>
                    )}
                    <Text fontSize="sm" color="gray.500">
                      {record.quarter} {record.year}
                    </Text>
                  </VStack>
                </Card>
              </GridItem>
            );
          })}
        </Grid>
      )}
    </Container>
  );
}